import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAdminGetKycQuery, useApproveKycMutation, useRejectKycMutation } from '../../features/kyc/kycApi';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { DataRow } from '../../components/ui/MobileCard';
import { SkeletonDetailCard } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import { authErrorMessage } from '../../lib/authErrorMessage';

const STATUS_TONE = { pending: 'warning', approved: 'success', rejected: 'danger' };

const DOCUMENT_TYPE_LABEL = {
  national_id: 'National ID',
  passport: 'Passport',
  drivers_license: "Driver's License",
  utility_bill: 'Utility Bill',
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function formatBytes(bytes) {
  const num = Number(bytes);
  if (!Number.isFinite(num)) return '—';
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(0)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * GET /admin/kyc/{submission} — approve/reject only render when status is
 * still 'pending' (both also 409 server-side on an already-reviewed
 * submission, but the button being gone entirely is the primary guard
 * against double-clicks, not the 409).
 */
export default function KycReviewDetail() {
  const { id } = useParams();
  const toast = useToast();
  const { data: submission, isLoading, isError, error } = useAdminGetKycQuery(id, { skip: !id });
  const [approveKyc, { isLoading: isApproving }] = useApproveKycMutation();
  const [rejectKyc, { isLoading: isRejecting }] = useRejectKycMutation();

  const [confirmApprove, setConfirmApprove] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(null);

  const isPending = submission?.status === 'pending';

  const runApprove = async () => {
    try {
      await approveKyc(id).unwrap();
      toast.push({ tone: 'success', message: 'Submission approved. The investor now has an active account.' });
      setConfirmApprove(false);
    } catch (err) {
      toast.push({
        tone: 'danger',
        title: 'Approve failed',
        message: authErrorMessage(err, { fallback: 'This submission may have already been reviewed.' }),
      });
      setConfirmApprove(false);
    }
  };

  const runReject = async () => {
    if (!reason.trim()) {
      setReasonError('A rejection reason is required.');
      return;
    }
    try {
      await rejectKyc({ id, reason: reason.trim() }).unwrap();
      toast.push({ tone: 'success', message: 'Submission rejected.' });
      setRejectOpen(false);
      setReason('');
    } catch (err) {
      toast.push({
        tone: 'danger',
        title: 'Reject failed',
        message: authErrorMessage(err, { fallback: 'This submission may have already been reviewed.' }),
      });
      setRejectOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/admin/kyc" className="text-sm text-accent hover:underline">
        ← Back to queue
      </Link>

      {isLoading && <SkeletonDetailCard fields={4} />}

      {isError && (
        <Card>
          <p className="py-6 text-center text-danger">{authErrorMessage(error, { notFound: 'Submission not found.' })}</p>
        </Card>
      )}

      {/* The request succeeded but the response didn't match a submission
          shape (see kycApi's unwrapSubmission) — a parsing bug, not a 404,
          so it gets its own message rather than silently rendering blank. */}
      {!isLoading && !isError && !submission && (
        <Card>
          <p className="py-6 text-center text-danger">
            Something went wrong loading this submission. Check the console for details.
          </p>
        </Card>
      )}

      {submission && (
        <Card className="space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-lg font-semibold text-foreground">{submission.user?.full_name || 'Investor'}</h1>
              <p className="text-sm text-foreground-muted">{submission.user?.email}</p>
            </div>
            <Badge tone={STATUS_TONE[submission.status] || 'neutral'}>{submission.status?.toUpperCase()}</Badge>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <DataRow label="Document type" value={DOCUMENT_TYPE_LABEL[submission.document_type] || submission.document_type} />
            <DataRow label="Submitted" value={formatDate(submission.created_at)} />
            {submission.reviewed_at && <DataRow label="Reviewed" value={formatDate(submission.reviewed_at)} />}
            {submission.reviewer && <DataRow label="Reviewed by" value={submission.reviewer.full_name} />}
          </div>

          {submission.status === 'rejected' && submission.rejection_reason && (
            <div className="rounded-xl border border-danger/30 bg-danger-soft p-3 text-sm text-danger">
              Rejection reason: {submission.rejection_reason}
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-foreground-muted mb-2">Documents</p>
            <div className="space-y-2">
              {submission.documents.length === 0 && <p className="text-sm text-foreground-muted">No documents attached.</p>}
              {submission.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background-elevated px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <p className="truncate text-foreground">{doc.original_name}</p>
                    <p className="text-xs text-foreground-subtle">
                      {doc.mime_type} · {formatBytes(doc.size)}
                    </p>
                  </div>
                  {doc.download_url && (
                    <a
                      href={doc.download_url}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 text-xs text-accent hover:underline"
                    >
                      View
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {isPending && (
            <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
              <Button size="sm" onClick={() => setConfirmApprove(true)} isLoading={isApproving}>
                Approve
              </Button>
              <Button size="sm" variant="danger" onClick={() => setRejectOpen(true)} isLoading={isRejecting}>
                Reject
              </Button>
            </div>
          )}
        </Card>
      )}

      <ConfirmDialog
        open={confirmApprove}
        onClose={() => setConfirmApprove(false)}
        onConfirm={runApprove}
        isLoading={isApproving}
        tone="secondary"
        title="Approve this submission?"
        confirmLabel="Approve"
        message="This activates the investor's account immediately and emails them the decision."
      />

      <Modal open={rejectOpen} onClose={() => setRejectOpen(false)} title="Reject submission">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-foreground-muted mb-1.5">Rejection reason</label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError(null);
              }}
              placeholder="Explain what's wrong so the investor knows what to fix"
              className="w-full rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus:border-border-strong"
            />
            {reasonError && <p className="mt-1.5 text-xs text-danger">{reasonError}</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={runReject} isLoading={isRejecting}>
              Reject submission
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
